// services/priceTagService.js (обновленная версия)
import api from './api';

/**
 * Сервис для работы с ценниками
 */
const priceTagService = {
  /**
   * Получение списка ценников с фильтрацией и пагинацией
   * @param {Object} params - Параметры запроса
   * @param {string} [params.productId] - ID товара
   * @param {number} [params.storeId] - ID магазина
   * @param {string} [params.taskId] - ID задачи
   * @param {boolean} [params.hasTask] - Фильтр по наличию задачи
   * @param {Date} [params.startDate] - Начальная дата
   * @param {Date} [params.endDate] - Конечная дата
   * @param {number} [params.page=1] - Номер страницы
   * @param {number} [params.pageSize=20] - Количество элементов на странице
   * @returns {Promise<Array>} - Список ценников
   */
  getPriceTags: async (params = {}) => {
    const { productId, storeId, startDate, endDate, category, page = 1, pageSize = 20 } = params;
    
    let queryParams = `page=${page}&pageSize=${pageSize}`;
    
    if (productId) {
      queryParams += `&productId=${productId}`;
    }
    
    if (storeId) {
      queryParams += `&storeId=${storeId}`;
    }
    
    if (startDate) {
      queryParams += `&startDate=${startDate.toISOString()}`;
    }
    
    if (endDate) {
      queryParams += `&endDate=${endDate.toISOString()}`;
    }
    
    if (category) {
      queryParams += `&category=${category}`;
    }
    
    const response = await api.get(`/price-tags?${queryParams}`);
    return response.data;
  },

  /**
   * Получение ценника по ID
   * @param {string} id - ID ценника
   * @returns {Promise<Object>} - Данные ценника
   */
  getPriceTagById: async (id) => {
    const response = await api.get(`/price-tags/${id}`);
    return response.data;
  },

  /**
   * Создание нового ценника
   * @param {Object} priceTagData - Данные нового ценника
   * @returns {Promise<Object>} - Данные созданного ценника
   */
  createPriceTag: async (priceTagData) => {
    const response = await api.post('/price-tags', priceTagData);
    return response.data;
  },

  /**
   * Обновление существующего ценника
   * @param {string} id - ID ценника
   * @param {Object} priceTagData - Данные для обновления
   * @returns {Promise<Object>} - Обновленные данные ценника
   */
  updatePriceTag: async (id, priceTagData) => {
    const response = await api.put(`/price-tags/${id}`, priceTagData);
    return response.data;
  },

  /**
   * Верификация ценника
   * @param {string} id - ID ценника
   * @returns {Promise<Object>} - Результат операции
   */
  verifyPriceTag: async (id) => {
    const response = await api.patch(`/price-tags/${id}/verify`);
    return response.data;
  },

  /**
   * Скрыть ценник (мягкое удаление)
   * @param {string} id - ID ценника
   * @returns {Promise<Object>} - Результат операции
   */
  toggleHidden: async (id) => {
    const response = await api.patch(`/price-tags/${id}/toggle-hidden`);
    return response.data;
  },

  /**
   * Загрузка изображения для ценника
   * @param {File} file - Файл изображения
   * @returns {Promise<Object>} - Данные загруженного изображения
   */
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/price-tags/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  },

  /**
   * Распознавание ценника с помощью AI
   * @param {string} imageId - ID изображения
   * @returns {Promise<Object>} - Результаты распознавания
   */
  recognizePriceTag: async (imageId) => {
    const response = await api.post('/price-tags/recognize', { imageId });
    return response.data;
  },

  /**
   * Получение изображений ценника
   * @param {string} priceTagId - ID ценника
   * @returns {Promise<Array>} Массив изображений ценника
   */
  getPriceTagImages: async (priceTagId) => {
    const response = await api.get(`/price-tags/${priceTagId}/images`);
    return response.data;
  },

  /**
   * Добавление изображения к ценнику
   * @param {string} priceTagId - ID ценника
   * @param {Object} imageData - Данные изображения
   * @returns {Promise<Object>} Данные добавленного изображения
   */
  addImageToPriceTag: async (priceTagId, imageData) => {
    const response = await api.post(`/price-tags/${priceTagId}/images`, imageData);
    return response.data;
  },

  /**
   * Удаление изображения из ценника
   * @param {string} priceTagId - ID ценника
   * @param {string} imageId - ID изображения
   * @returns {Promise<boolean>} Результат операции
   */
  removeImageFromPriceTag: async (priceTagId, imageId) => {
    const response = await api.delete(`/price-tags/${priceTagId}/images/${imageId}`);
    return response.data;
  },

  /**
   * Установка основного изображения ценника
   * @param {string} priceTagId - ID ценника
   * @param {string} imageId - ID изображения
   * @returns {Promise<boolean>} Результат операции
   */
  setMainImage: async (priceTagId, imageId) => {
    const response = await api.patch(`/price-tags/${priceTagId}/images/${imageId}/set-main`);
    return response.data;
  },

  /**
   * Получение истории ценников для товара в конкретном магазине
   * @param {string} productId - ID товара
   * @param {number} storeId - ID магазина
   * @param {number} limit - Максимальное количество ценников (по умолчанию 5)
   * @returns {Promise<Array>} - Массив предыдущих ценников
   */
  getPriceTagHistory: async (productId, storeId, limit = 5) => {
    const response = await api.get('/price-tags/history', {
      params: { productId, storeId, limit }
    });
    return response.data;
  },

  /**
   * Получение presigned URL для изображения из S3
   * @param {string} imageId - ID изображения
   * @param {number} expirationMinutes - Время жизни URL в минутах (по умолчанию 60)
   * @returns {Promise<string>} - Presigned URL
   */
  getImagePresignedUrl: async (imageId, expirationMinutes = 60) => {
    const response = await api.get(`/images/${imageId}/url`, {
      params: { expirationMinutes }
    });
    return response.data.url;
  }
};

export default priceTagService;